import { useState, useEffect, useCallback } from 'react';
import { notification } from 'antd';
import { StageInfo } from '../types/stageTypes';
import {
    getAtsStatus,
    getDefaultStageMappings
} from '../../../api/integrations';

/**
 * Custom hook for fetching and managing stage data
 * @returns State and functions related to stage data fetching
 */
export const useStageData = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalStages, setOriginalStages] = useState<StageInfo[]>([]);
    const [provider, setProvider] = useState<string | null>(null);
    const [atsConnected, setAtsConnected] = useState(true);
    const [noJobsConnected, setNoJobsConnected] = useState(false);

    // Function to fetch stage data from the API
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // First check ATS connection status
            const atsStatus = await getAtsStatus();

            if (!atsStatus || atsStatus.state !== 'CONNECTED') {
                setAtsConnected(false);
                setLoading(false);
                return;
            }

            setAtsConnected(true);

            // Check if there are any job connections
            if (!atsStatus.job_connections || atsStatus.job_connections.length === 0) {
                setNoJobsConnected(true);
                setLoading(false);
                return;
            }

            // Extract provider ID from the first job connection
            const firstJobId = atsStatus.job_connections[0].ats_job.id;
            const providerId = firstJobId.split(':')[0];
            setProvider(providerId);

            // Get stage mappings from the backend
            const mappings = await getDefaultStageMappings();

            if (!mappings || mappings.length === 0) {
                setOriginalStages([]);
                setLoading(false);
                return;
            }

            // Create a map to deduplicate stages by name (case insensitive)
            const stagesByName = new Map<string, StageInfo>();

            // First, sort mappings to prioritize those with stage_type
            const sortedMappings = [...mappings].sort((a, b) => {
                // Prioritize mappings with stage_type over those without
                if (a.stage_type && !b.stage_type) return -1;
                if (!a.stage_type && b.stage_type) return 1;
                // If both have stage_type or both don't, prioritize those with id
                if (a.id && !b.id) return -1;
                if (!a.id && b.id) return 1;
                return 0;
            });

            sortedMappings.forEach(mapping => {
                // Prioritize external_stage_name, fallback to external_name
                const stageName = mapping.external_stage_name || mapping.external_name;
                const stageId = mapping.external_stage_name ? mapping.external_stage_name : mapping.external_name;
                const normalizedName = stageName.toLowerCase().trim(); // Normalize for deduplication key

                // Keep the stage if:
                // 1. We haven't seen this name before, OR
                // 2. The existing stage doesn't have a stage_type but this one does
                const existingStage = stagesByName.get(normalizedName);
                const shouldReplace =
                    !existingStage ||
                    (!existingStage.mapping.stage_type && mapping.stage_type);

                if (shouldReplace) {
                    stagesByName.set(normalizedName, {
                        id: stageId,
                        name: stageName,
                        mapping: mapping,
                        isNew: !mapping.id,
                        isChanged: false
                    });
                }
            });

            // Convert to array and set state
            const uniqueStages = Array.from(stagesByName.values());
            setOriginalStages(uniqueStages);
        } catch (error) {
            console.error('Error fetching stages:', error);
            notification.error({
                message: 'Error',
                description: 'There was an error loading the stage data.',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // Load data when the component mounts
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Set the saving state
    const setSavingState = (isSaving: boolean) => {
        setSaving(isSaving);
    };

    return {
        loading,
        saving,
        originalStages,
        provider,
        atsConnected,
        noJobsConnected,
        setSavingState,
        fetchData
    };
}; 